import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';
import { useAuth } from 'context/AuthContext';

const SalesCompanyProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState(true);
  const [savingContact, setSavingContact] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [contactForm, setContactForm] = useState({
    full_name: '',
    email: '',
    role: '',
    phone: ''
  });

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const [companyResponse, contactsResponse, opportunitiesResponse] = await Promise.all([
          axiosInstance.get(`sales/companies/${companyId}/`),
          axiosInstance.get(`sales/companies/${companyId}/contacts/`),
          axiosInstance.get('sales/opportunities/', { params: { company_id: companyId } })
        ]);

        if (!mounted) return;

        const companyData = companyResponse.data?.company || null;
        const companyOpportunities = opportunitiesResponse.data?.opportunities || [];

        setCompany(companyData);
        setContacts(contactsResponse.data?.contacts || []);
        setOpportunities(companyOpportunities);
      } catch (requestError) {
        if (!mounted) return;
        setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_COMPANY'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [companyId, t]);

  const createContact = async event => {
    event.preventDefault();
    setSavingContact(true);
    setError('');
    setSuccess('');

    try {
      const response = await axiosInstance.post(`sales/companies/${companyId}/contacts/`, contactForm);
      const newContact = response.data?.contact;
      if (newContact) {
        setContacts(previous => [...previous, newContact]);
      }
      setContactForm({ full_name: '', email: '', role: '', phone: '' });
      setSuccess(t('SALES.CONTACT_CREATED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CREATE_CONTACT'));
    } finally {
      setSavingContact(false);
    }
  };

  const stats = useMemo(() => {
    const openDeals = opportunities.filter(item => item.status === 'open').length;
    const wonDeals = opportunities.filter(item => item.status === 'won').length;
    const hotDeals = opportunities.filter(item => item.interest_level === 'hot' && item.status === 'open').length;
    return { openDeals, wonDeals, hotDeals };
  }, [opportunities]);

  const canGenerateSalesLinks = hasPermission('crm.generate_link');
  const toAbsoluteUrl = path => `${window.location.origin}${path}`;

  const generateCompanyLink = async () => {
    setGeneratingLink(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post(`sales/companies/${companyId}/sales-link/`);
      setGeneratedLink(response.data?.link || null);
      setSuccess(t('SALES.SALES_LINK_CREATED', { defaultValue: 'Sales link created' }));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CREATE_LINK', { defaultValue: 'Unable to create sales link' }));
    } finally {
      setGeneratingLink(false);
    }
  };

  return (
    <>
      <PageHeader title={t('SALES.COMPANY_PROFILE')} className="mb-3" />

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      {loading ? (
        <Card className="p-4 text-center">
          <Spinner animation="border" />
        </Card>
      ) : (
        <>
          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                <div>
                  <h4 className="mb-1">{company?.name || `#${companyId}`}</h4>
                  <div className="text-600 fs--1">{company?.industry || '-'}</div>
                  <div className="text-600 fs--1">{company?.website || '-'}</div>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
                  {canGenerateSalesLinks ? (
                    <Button size="sm" variant="outline-dark" onClick={generateCompanyLink} disabled={generatingLink}>
                      {generatingLink ? t('SALES.SAVING') : t('SALES.GENERATE_LINK', { defaultValue: 'Generate link' })}
                    </Button>
                  ) : null}
                  <Badge bg={company?.status === 'customer' ? 'success' : company?.status === 'active_prospect' ? 'warning' : 'secondary'}>
                    {company?.status || '-'}
                  </Badge>
                </div>
              </div>
            </Card.Body>
          </Card>

          {generatedLink ? (
            <Alert variant="info">
              <div className="fw-semi-bold mb-2">{t('SALES.LINK_READY', { defaultValue: 'Lead links ready' })}</div>
              <div className="small">{toAbsoluteUrl(generatedLink.waitlist_path)}</div>
              <div className="small">{toAbsoluteUrl(generatedLink.register_path)}</div>
            </Alert>
          ) : null}

          <Row className="g-3 mb-3">
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.OPEN_OPPORTUNITIES')}</div>
                  <div className="fs-2 fw-bold">{stats.openDeals}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.HOT_OPPORTUNITIES')}</div>
                  <div className="fs-2 fw-bold text-warning">{stats.hotDeals}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.WON_DEALS')}</div>
                  <div className="fs-2 fw-bold text-success">{stats.wonDeals}</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col xl={7}>
              <Card>
                <Card.Header className="fw-semi-bold">{t('SALES.OPPORTUNITIES')}</Card.Header>
                <Card.Body className="pt-2">
                  {opportunities.length ? opportunities.map(item => (
                    <div key={item.id} className="py-2 border-bottom d-flex justify-content-between align-items-center gap-2">
                      <div>
                        <div className="fw-semi-bold">{item.title}</div>
                        <div className="text-600 fs--1">{item.pipeline_stage_name || t('SALES.NO_STAGE')}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => navigate(`/sales/opportunity/${item.id}`)}
                      >
                        {t('SALES.OPEN_DEAL')}
                      </Button>
                    </div>
                  )) : <div className="text-600">{t('SALES.EMPTY_OPPORTUNITIES')}</div>}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={5}>
              <Card className="mb-3">
                <Card.Header className="fw-semi-bold">{t('SALES.CONTACTS')}</Card.Header>
                <Card.Body className="pt-2">
                  {contacts.length ? (
                    <Table responsive size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>{t('SALES.CONTACT_NAME')}</th>
                          <th>{t('SALES.CONTACT_EMAIL')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map(contact => (
                          <tr key={contact.id}>
                            <td>{contact.full_name}</td>
                            <td>{contact.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <div className="text-600">{t('SALES.EMPTY_CONTACTS')}</div>
                  )}
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="fw-semi-bold">{t('SALES.ADD_CONTACT')}</Card.Header>
                <Card.Body>
                  <Form onSubmit={createContact}>
                    <Form.Group className="mb-2">
                      <Form.Label>{t('SALES.CONTACT_NAME')}</Form.Label>
                      <Form.Control
                        value={contactForm.full_name}
                        onChange={event => setContactForm(previous => ({ ...previous, full_name: event.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>{t('SALES.CONTACT_EMAIL')}</Form.Label>
                      <Form.Control
                        type="email"
                        value={contactForm.email}
                        onChange={event => setContactForm(previous => ({ ...previous, email: event.target.value }))}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>{t('SALES.CONTACT_ROLE')}</Form.Label>
                      <Form.Control
                        value={contactForm.role}
                        onChange={event => setContactForm(previous => ({ ...previous, role: event.target.value }))}
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('SALES.PHONE')}</Form.Label>
                      <Form.Control
                        value={contactForm.phone}
                        onChange={event => setContactForm(previous => ({ ...previous, phone: event.target.value }))}
                      />
                    </Form.Group>
                    <Button type="submit" disabled={savingContact}>
                      {savingContact ? t('SALES.SAVING') : t('SALES.SAVE')}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default SalesCompanyProfilePage;
